/**
 * Выбор шаблона задачи на новом бэке — `/v2/tasks/templates`.
 *
 * Отдельный компонент, а не проп к `DTemplateDropdown`: тот сам ходит в
 * legacy-API и типизирован под его запись (`t.ID`, `t.TITLE` из Bitrix), а в v2
 * у шаблона `id`/`name` и вложенный `payload`. Внешний вид повторён — та же
 * пилюля, та же панель, тот же поиск.
 *
 * Шаблон здесь именно **применяется к форме**, а не создаёт задачу сразу:
 * `payload` повторяет поля формы, пользователь после подстановки может всё
 * поправить. Создание задачи прямо из шаблона (`instantiate`) — другой сценарий,
 * ему место в списке задач, а не в форме.
 */
import { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, Loader2 } from 'lucide-react';

import type { TaskTemplateV2 } from '@/entities/task/api/task-automation-v2';
import { useTaskTemplatesV2 } from '@/entities/task/model/useTaskAutomationV2';
import { useOutsideClick } from '@/hooks/ui/useOutsideClick';
import { FloatingPanel } from '@/shared/ui/FloatingPanel';

import { onAltArrowDownOpen } from '../utils';

interface Props {
  disabled?: boolean;
  onApply: (template: TaskTemplateV2) => void;
}

export function TaskV2TemplateDropdown({ disabled, onApply }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<TaskTemplateV2 | null>(null);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Список тянем только когда панель открыли — форму это не задерживает.
  const templatesQ = useTaskTemplatesV2(open && !disabled);

  useOutsideClick({
    enabled: open,
    refs: [btnRef, panelRef],
    onOutside: () => setOpen(false),
  });

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const list = templatesQ.data ?? [];
    if (!s) {
      return list;
    }

    return list.filter((t) => (t.name ?? '').toLowerCase().includes(s));
  }, [templatesQ.data, q]);

  return (
    <div ref={wrapRef} className="relative">
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={onAltArrowDownOpen(open, () => setOpen(true))}
        aria-expanded={open}
        aria-haspopup="menu"
        className={clsx(
          'inline-flex h-[44px] items-center gap-[10px] rounded-[24px] bg-white px-[18px]',
          'border border-black/5 shadow-[0_3.54px_12.88px_rgba(0,0,0,0.06)]',
          'text-[16px] leading-[130%] tracking-[-0.83px] text-[#000000]',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <span className="max-w-[280px] truncate">
          {selected?.name || 'Выбрать шаблон'}
        </span>
        <ChevronDown
          className={clsx(
            'h-[14px] w-[14px] shrink-0 transition',
            open && 'rotate-180'
          )}
        />
      </button>

      {open ? (
        <FloatingPanel
          ref={panelRef}
          anchorRef={wrapRef}
          align="end"
          sideOffset={8}
          role="menu"
          className="w-[360px] max-w-[calc(100vw-48px)] rounded-[12px] border border-black/10 bg-white p-2 shadow-[0_12px_30px_rgba(0,0,0,0.18)]"
        >
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск шаблона"
            className="mb-2 h-[36px] w-full rounded-[8px] border border-black/10 px-[10px] text-[14px] outline-none"
          />

          {templatesQ.isPending ? (
            <div className="flex items-center gap-[8px] px-[10px] py-[12px] text-[13px] text-[#8a8a8a]">
              <Loader2 className="h-[14px] w-[14px] animate-spin" />
              Загружаю шаблоны…
            </div>
          ) : templatesQ.isError ? (
            <p className="px-[10px] py-[12px] text-[13px] text-[#d33]">
              Не удалось загрузить шаблоны
            </p>
          ) : filtered.length === 0 ? (
            <p className="px-[10px] py-[12px] text-[13px] text-[#8a8a8a]">
              {q.trim() ? 'Ничего не найдено' : 'Шаблонов пока нет'}
            </p>
          ) : (
            <ul className="max-h-[280px] overflow-y-auto">
              {filtered.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(t);
                      setOpen(false);
                      onApply(t);
                    }}
                    className="w-full truncate rounded-[8px] px-[10px] py-[8px] text-left text-[14px] hover:bg-black/[0.04]"
                  >
                    {t.name || 'Без названия'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </FloatingPanel>
      ) : null}
    </div>
  );
}
